import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material";

import { SamlAliasView, SAML } from "@impulse/sdp-shared-models";

import { BrowserTitleService } from "../../common/browser-title/browser-title.service";
import { TranslateService } from "../../common/translate/translate.service";
import { SnackbarService } from "../../common/snackbar/snackbar.service";
import { GenericTable } from "../../genericTable";
import { SamlAliasesService } from "./saml-aliases.service";
import { SamlAliasEditComponent } from "./saml-alias-wizard/saml-alias-edit/saml-alias-edit.component";
import { WizardDialogService } from "../wizard-dialog/wizard-dialog.service";
import { tableEditIcon, tableDeleteIcon } from "../../util";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { PolicyWizardService } from "../policies/policy-wizard";

@Component({
	selector: "sdp-app-saml-aliases",
	templateUrl: "./saml-aliases.component.html"
})
export class SamlAliasesComponent implements OnInit {
	public isDataLoaded = false;
	public data: SamlAliasView[] | undefined = undefined;
	public providerData: SAML.IdentityProvider[] = [];

	public columnSchema: GenericTable.ColumnSchema[] = [];


	constructor(
		public dialog: MatDialog,
		private service: SamlAliasesService,
		private snackbarService: SnackbarService,
		private titleService: BrowserTitleService,
		private translateService: TranslateService,
		private wizardDialogService: WizardDialogService,
		private policyWizardService: PolicyWizardService
	) {
		this.columnSchema = this.getSchema();
	}

	public async ngOnInit() {
		this.titleService.setTitle("sdp.label.saml-alias");
		this.isDataLoaded = false;
		await this.refresh();
	}


	public showCreateSamlAlias() {
		// set property for alias under change
		this.service.newSamlAlias();
		this.openSamlAliasEditDialog(true);
	}

	public editSamlAliasDialog(alias: SamlAliasView) {
		// set property for alias under change
		this.service.editSamlAlias(alias);
		this.openSamlAliasEditDialog(false);
	}


	public async showDeleteSamlAlias(alias: SamlAliasView) {
		let message = this.translateService.translate(
			"saml-alias.delete.dialog.message",
			{
				aliasLabel: alias.label
			}
		);
		if (await this.isUsedInPolicy(alias)) {
			message += this.translateService.translate("saml-alias.delete.dialog.inUse.message");
		}

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {});
		dialogRef.componentInstance.message = message;
		dialogRef.componentInstance.title = "saml-alias.delete.dialog.title";
		dialogRef.componentInstance.buttonAction = "sdp.label.delete";
		dialogRef.componentInstance.buttonCancel = "sdp.label.cancel";
		dialogRef.afterClosed().subscribe(async (result: boolean) => {
			if (result) {
				try {
					this.isDataLoaded = false;
					const successMessage = await this.service.deleteSamlAlias(alias);
					if (message.length > 0) {
						this.snackbarService.open(successMessage);
					}
				} catch (e) {
					this.snackbarService.open(this.translateService.translate(e));
				} finally {
					this.isDataLoaded = true;
					await this.refresh();
				}
			}
		});
	}

	public getSchema() {
		const columnSchema: GenericTable.ColumnSchema[] = [];

		columnSchema.push({
			minWidth: "30%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "saml-alias.table.label"
			},
			bodySchema: {
				type: "string",
				boundField: "label"
			}
		});
		columnSchema.push({
			minWidth: "30%",
			type: "string",
			headerSchema: {
				type: "basic",
				headerText: "saml-alias.table.saml-group",
				sortable: true
			},
			bodySchema: {
				type: "string",
				boundField: "samlGroup"
			}
		});
		columnSchema.push({
			minWidth: "30%",
			type: "string",
			headerSchema: {
				type: "basic",
				headerText: "saml-alias.table.provider",
				sortable: true
			},
			bodySchema: {
				type: "string",
				boundField: "providerId",
				displayCallback: (row: SamlAliasView) => {
					return this.service.getProviderName(row, this.providerData);
				}
			}
		});
		columnSchema.push({
			minWidth: "10%",
			type: "button",
			headerSchema: {
				type: "none",
			},
			bodySchema: {
				type: "button",
				buttons: this.getButtons()
			}
		});
		return columnSchema;
	}

	private async refresh() {
		try {
			this.isDataLoaded = false;

			const providerPromise = this.service.getProviders();
			const listPromise = this.service.getList();
			this.providerData = await providerPromise;
			this.data = await listPromise;
			this.data.sort((a, b) => {
				let sortie = this.service.getProviderNameById(this.providerData, a.providerId)
					.localeCompare(this.service.getProviderNameById(this.providerData, b.providerId));
				if (sortie === 0) {
					sortie = a.label.localeCompare(b.label);
				}
				return sortie;
			});
		} catch (e) {
			this.snackbarService.open(this.translateService.translate(e));
		} finally {
			this.isDataLoaded = true;
		}
	}

	private getButtons() {
		const buttons: GenericTable.Button[] = [];
		buttons.push({
			type: "icon",
			icon: tableEditIcon,
			onClick: (row: SamlAliasView) => { return this.editSamlAliasDialog(row); }
		});
		buttons.push({
			type: "icon",
			icon: tableDeleteIcon,
			onClick: (row: SamlAliasView) => { return this.showDeleteSamlAlias(row); }
		});
		return buttons;
	}

	private openSamlAliasEditDialog(isCreate: boolean) {
		const dialogRef = this.wizardDialogService.openDialog(
			SamlAliasEditComponent,
			{ type: "material", name: "person", cssClass: "sdp-section-header-icon" },
			isCreate
		);
		dialogRef.afterClosed().subscribe(async () => {
			await this.refresh();
		});
	}

	private async isUsedInPolicy(alias: SamlAliasView) {
		const policies = await this.policyWizardService.getAllPolicies();
		const inUse = policies.find((p) => p.access.samlAliases.indexOf(alias.id) >= 0);
		return !!inUse;
	}

}
